import { alpha, Box, Divider, Popover, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../../hooks/useStore';
import { LogoutButton } from './logoutButton';
import { MenuItems } from './menuItems';
import { ProfileData } from './profileData';
import { LegalItems } from './legalItems';
import { UserCompany } from '@pxtra-client-lib-legacy/gen';

export type CurrentUserData = {
  first_name: string;
  last_name: string;
  email: string;
  user_config: {
    avatar_url: string;
    language: string;
    selected_company: number;
  };
  id: number;
};

export const AvatarMenu: FC = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const closeAvatarMenu = () => setAnchorEl(null);
  const { companyStore, authenticationStore } = useStore();
  const queryClient = useQueryClient();

  const isEmployeeProfilePath = new RegExp(/^\/employees\/.*/);
  const isEmployeesAction = new RegExp(
    /^\/actions\/(create-employee|update-employees|update-budget)\/.*/
  );
  const isImpexAction = new RegExp(/^\/(actions\/create-import|exports)\/.*/);
  const isBenefitSetAction = new RegExp(/^\/actions\/benefit-set\/.*/);
  const isBonusAction = new RegExp(/^\/actions\/bonus\/.*/);

  const isOnboarding = new RegExp(/^\/actions\/onboarding\/.*/);
  const isPortfolioPath = new RegExp(/^\/benefits\/portfolio\/.*/);

  const navigateToOverview = (path: string) => {
    if (isEmployeeProfilePath.test(path) || isEmployeesAction.test(path)) {
      navigate('/employees');
    } else if (isImpexAction.test(path)) {
      navigate('/exports/overview');
    } else if (isBenefitSetAction.test(path)) {
      navigate('/benefits/benefit-set');
    } else if (isBonusAction.test(path)) {
      navigate('/bonus');
    } else if (isOnboarding.test(path)) {
      navigate('dashboard');
    } else if (isPortfolioPath.test(path)) {
      navigate('benefits');
    }
  };

  const selectCompany = async (company: UserCompany) => {
    closeAvatarMenu();
    companyStore.fetchBenefitSettings();
    await companyStore.setCurrentCompanyById(company.id);
    queryClient.removeQueries({ queryKey: ['employees'] });
    queryClient.removeQueries({ queryKey: ['employee'] });
    navigateToOverview(location.pathname);
  };

  const changeLanguage = async (language: 'de' | 'en') => {
    await authenticationStore.setLanguage(language);
    closeAvatarMenu();
  };

  const changePasswordClicked = () => {
    navigate('/company/account/password');
    closeAvatarMenu();
  };

  const manageTwoFactorAuthClicked = () => {
    navigate('/company/account/twofactor');
    closeAvatarMenu();
  };

  const manageAppLoginClicked = () => {
    navigate('/company/security/app');
    closeAvatarMenu();
  };

  const managePlatformLoginClicked = () => {
    navigate('/company/security/platform');
    closeAvatarMenu();
  };

  const kundenWerbenKundenClicked = () => {
    navigate('/kunden-werben-kunden');
    closeAvatarMenu();
  };

  return (
    <>
      <Box
        data-testid="company-menu-button"
        className="flex text-primary-main cursor-pointer border border-solid border-grey-100 rounded-lg px-6 py-3 hover:bg-grey-50"
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      >
        <Typography fontWeight="bold">
          {companyStore.company ? companyStore.company.name : t('common:LOADING')}
        </Typography>
      </Box>
      <Popover
        id="avatar-menu"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeAvatarMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        BackdropProps={{
          invisible: true,
          style: { backgroundColor: 'transparent', opacity: '0' }
        }}
        PaperProps={{
          className: 'shadow-500'
        }}
        sx={(theme) => ({
          '& .MuiPopover-paper': {
            borderRadius: theme.spacing(1),
            boxShadow: `4px 4px 8px ${alpha(theme.palette.primary.light, 0.5)}`,
            marginTop: 1,
            marginLeft: '-24px'
          }
        })}
      >
        <>
          <ProfileData
            companyName={companyStore.company?.name ?? ''}
            email={authenticationStore.currentUser ?? ''}
          />
          <Divider className="text-grey-100" />
          <MenuItems
            managedCompanies={companyStore.managedCompanies}
            selectedCompany={companyStore.company}
            onSelectCompany={selectCompany}
            onSelectLanguage={changeLanguage}
            onPasswordChangeClicked={changePasswordClicked}
            onManageTwoFactorAuthClicked={manageTwoFactorAuthClicked}
            onManageAppLoginClicked={manageAppLoginClicked}
            onManagePlatformLoginClicked={managePlatformLoginClicked}
            onKundenWerbenKundenClicked={kundenWerbenKundenClicked}
          />
          <Divider className="text-grey-100" />
          <LegalItems />
          <Divider
            sx={(theme) => ({
              color: theme.palette.grey['100']
            })}
          />
          <LogoutButton />
        </>
      </Popover>
    </>
  );
});
